import type { CellContext } from '@tanstack/react-table';
import { nanoid } from 'nanoid';
import * as React from 'react';
import type { Path } from 'react-hook-form';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';

import type { ColorPalette } from '@acceligentllc/shared/enums/color';

import type { FooterButton } from 'af-components/Controls/SimpleTable/types';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { validateFieldArray, validateOneField } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper/validationRHF';
import ColorPickerCellRenderer from 'af-components/RHFTableCellRenderers/ColorPickerCellRenderer';
import InputCellRenderer from 'af-components/RHFTableCellRenderers/InputCellRenderer';

import SortGroupsModal from './SortGroupsModal';
import styles from './styles.module.scss';
import type { BillingCodeGroupFM } from '../../formModel';

export type BillingCodeGroupRow = BillingCodeGroupFM & SimpleTableRow;

export interface FormValues {
	billingCodeGroups: BillingCodeGroupRow[];
}

export interface BillingCodeGroupsArrayProps {
	initialGroups: BillingCodeGroupFM[];
	onChange?: (groups: BillingCodeGroupFM[]) => void;
}

function makeSignature(
	groups: Array<Pick<BillingCodeGroupFM, 'id' | 'name' | 'color' | 'databaseId' | 'sortIndex' | 'deletedAt'>>
): string {
	return JSON.stringify(
		(groups ?? []).map((g) => ([
			g.id ?? '',
			g.databaseId ?? '',
			(g.name ?? '').trim(),
			g.color ?? '',
			g.sortIndex ?? '',
			g.deletedAt ?? '',
		]))
	);
}

function toRows(groups: BillingCodeGroupFM[]): BillingCodeGroupRow[] {
	return (groups ?? []).map((group, idx) => ({
		...group,
		index: idx,
	}));
}

function stripRow(row: BillingCodeGroupRow): BillingCodeGroupFM {
	return {
		id: row.id,
		databaseId: row.databaseId,
		name: row.name,
		color: row.color,
		sortIndex: row.sortIndex,
		deletedAt: row.deletedAt,
	};
}

const uniqueFields: (keyof BillingCodeGroupRow)[] = ['name'];
const requiredFields: (keyof BillingCodeGroupRow)[] = ['name', 'sortIndex'];
const numberFields: (keyof BillingCodeGroupRow)[] = ['sortIndex'];

const fieldNameMapper: Record<(keyof BillingCodeGroupRow), string> = {
	id: 'ID',
	databaseId: 'Database ID',
	name: 'Name',
	color: 'Color',
	sortIndex: 'Sort Index',
	index: 'Index',
	deletedAt: 'Deleted At',
};

type GroupColumn = keyof BillingCodeGroupFM;

function asPath<T>(p: string): Path<T> {
	return p as Path<T>;
}

function parseGroupPath(name: string): Nullable<{ index: number; field: GroupColumn; }> {
	// expecting: billingCodeGroups.<index>.<field>
	const parts = name.split('.');
	if (parts.length !== 3) {
		return null;
	}
	if (parts[0] !== 'billingCodeGroups') {
		return null;
	}

	const index = +parts[1];
	if (!Number.isInteger(index) || index < 0) {
		return null;
	}

	const field = parts[2] as GroupColumn;
	if (field !== 'name' && field !== 'color' && field !== 'id' && field !== 'databaseId' && field !== 'sortIndex') {
		return null;
	}

	return { index, field };
}

const getColorName = (idx: number) => {
	return `billingCodeGroups.${idx}.color` as const;
};

const BillingCodeGroupsArray: React.FC<BillingCodeGroupsArrayProps> = React.memo((props) => {
	const { onChange, initialGroups } = props;

	const [showSortModal, setShowSortModal] = React.useState(false);

	const {
		control,
		getValues,
		reset,
		watch,
		unregister,
		setError,
		clearErrors,
	} = useForm<FormValues>({
		defaultValues: { billingCodeGroups: toRows(initialGroups ?? []) },
		mode: 'onChange',
	});

	const { fields, append, remove, replace } = useFieldArray({
		control,
		name: 'billingCodeGroups',
	});

	// prevents firing onChange immediately after we programmatically reset()
	const isApplyingExternalRef = React.useRef(false);

	const openSortModal = React.useCallback(async () => {
		setShowSortModal(true);
	}, []);

	const closeSortModal = React.useCallback(() => {
		setShowSortModal(false);
	}, []);

	const initialSig = React.useMemo(() => makeSignature(initialGroups ?? []), [initialGroups]);

	// signature of RHF current state (used for comparisons)
	const currentFormSig = React.useCallback(() => {
		const current = (getValues('billingCodeGroups') ?? []).map(stripRow);
		return makeSignature(current);
	}, [getValues]);

	const emitGroupsToParent = React.useCallback(() => {
		if (!onChange) {
			return;
		}
		if (isApplyingExternalRef.current) {
			return;
		}

		const next = (getValues('billingCodeGroups') ?? []).map(stripRow);
		onChange(next);
	}, [getValues, onChange]);

	const validateBillingCodesGroupsArray = React.useCallback(() => {
		const values = getValues();

		clearErrors();
		validateFieldArray(values, setError, {
			requiredFields,
			numberFields,
			uniqueFields,
			fieldNameMapper,
			arrayKey: 'billingCodeGroups',
			uniqueCaseSensitive: true,
		});
	}, [getValues, clearErrors, setError]);

	const validateOnChange = React.useCallback(
		(name: string) => {
			const parsed = parseGroupPath(name);
			if (!parsed) {
				return;
			}

			const { index, field } = parsed;
			const cellPath = `billingCodeGroups.${index}.${field}`;

			clearErrors(asPath<FormValues>(cellPath));

			const value = getValues(asPath<FormValues>(cellPath));

			if (uniqueFields.includes(field)) {
				validateBillingCodesGroupsArray();
				return;
			}

			validateOneField({
				cellName: cellPath,
				fieldName: field,
				value,
				setError,
				config: {
					requiredFields,
					numberFields,
					fieldNameMapper,
					arrayKey: 'billingCodeGroups',
				},
			});
		},
		[clearErrors, getValues, setError, validateBillingCodesGroupsArray]
	);

	const onGroupsCellChange = React.useCallback(
		(name: string) => {
			const parsed = parseGroupPath(name);
			if (!parsed) {
				return;
			}

			validateOnChange(name);
			emitGroupsToParent();
		},
		[emitGroupsToParent, validateOnChange]
	);

	React.useEffect(() => {
		const formSig = currentFormSig();

		if (initialSig === formSig) {
			return; // already in sync, do nothing
		}

		isApplyingExternalRef.current = true;
		reset({ billingCodeGroups: toRows(initialGroups ?? []) });

		// next tick re-enable onChange
		queueMicrotask(() => {
			isApplyingExternalRef.current = false;
		});
	}, [initialSig, initialGroups, reset, currentFormSig]);

	const groupsWatch = watch('billingCodeGroups');

	React.useEffect(() => {
		if (!onChange) {
			return;
		}
		if (isApplyingExternalRef.current) {
			return;
		}

		const next = (groupsWatch ?? []).map(stripRow);
		const nextSig = makeSignature(next);

		// If RHF equals initialGroups content, don't echo it back
		if (nextSig === initialSig) {
			return;
		}

		onChange(next);
	}, [groupsWatch, onChange, initialSig]);

	const reindex = React.useCallback(() => {
		const current = getValues('billingCodeGroups') ?? [];

		const ordered = [...current].sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

		const next = ordered.map((row, idx) => ({
			...row,
			sortIndex: idx + 1,
			index: idx,
		}));

		replace(next);
	}, [getValues, replace]);

	const addNewGroup = React.useCallback(async () => {
		append({
			id: nanoid(),
			databaseId: null,
			sortIndex: fields.length + 1,
			name: '',
			color: null,
			index: fields.length,
			deletedAt: null,
		});
		requestAnimationFrame(reindex);
	}, [append, fields.length, reindex]);

	const onSaveSortGroups = React.useCallback((sortedGroups: Array<Pick<BillingCodeGroupFM, 'id' | 'databaseId' | 'name' | 'color' | 'sortIndex'>>) => {
		const nextIndexByName = sortedGroups.reduce<Record<string, number>>((acc, g, idx) => {
			acc[g.name] = idx;
			return acc;
		}, {});

		const current = (getValues('billingCodeGroups') ?? []);

		// Reorder current rows by the new order
		const reordered = [...current].sort((a, b) => {
			const ai = nextIndexByName[a.name];
			const bi = nextIndexByName[b.name];
			return (ai ?? 999) - (bi ?? 999);
		});

		// Normalize indices to be contiguous 0..n-1
		const reindexed = reordered.map((groupRow, idx) => ({
			...groupRow,
			sortIndex: idx + 1,
			index: idx,
		}));

		replace(reindexed);
		onChange?.(reindexed.map(stripRow));

		closeSortModal();
	}, [closeSortModal, getValues, onChange, replace]);

	const renderInputCell = React.useCallback(
		(cellName: keyof BillingCodeGroupRow, cellType: 'text' | 'numeric' | 'dollar' | 'quantity') =>
			(cell: CellContext<BillingCodeGroupRow & SimpleTableRow, string>) => {
				const idx = cell.row.index;
				return (
					<InputCellRenderer
						control={control}
						defaultValue={(cell.getValue()) ?? ''}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`billingCodeGroups.${idx}.${cellName}`}
						onChange={onGroupsCellChange}
						rhfId={cell.row.original.id}
						type={cellType}
						unregister={unregister}
					/>
				);
			},
		[control, fields.length, getValues, onGroupsCellChange, unregister]
	);

	const renderTextCell = React.useCallback(
		() => (cell: CellContext<BillingCodeGroupRow, string>) => {
			return (
				<span className={styles['index-column']}>{+cell.getValue()}</span>
			);
		},
		[]
	);

	const renderColorCell = React.useCallback(
		() => (cell: CellContext<BillingCodeGroupRow, unknown>) => {
			const idx = cell.row.index;
			const fieldName = getColorName(idx);

			const defaultColor = cell.getValue() as Nullable<ColorPalette>;

			return (
				<div className={styles['color-input-container']}>
					<ColorPickerCellRenderer<FormValues, typeof fieldName>
						align="center"
						control={control}
						defaultValue={defaultColor}
						disabled={false}
						fieldsLength={fields.length}
						fixed={true}
						getValues={getValues}
						name={fieldName}
						onChange={onGroupsCellChange}
						position="left"
						rhfId={cell.row.original.id}
						showAdditionalOptions={true}
						unregister={unregister}
					/>
				</div>
			);
		},
		[control, fields.length, getValues, onGroupsCellChange, unregister]
	);

	const columns: SimpleTableProps<BillingCodeGroupRow>['columns'] = React.useMemo(
		() => [
			{
				id: 'sortIndex',
				accessor: 'sortIndex',
				header: '',
				enableSorting: false,
				cell: renderTextCell(),
				size: '40px',
			},
			{
				id: 'name',
				accessor: 'name',
				header: () => <div className={styles['group-name-header']}>Name*</div>,
				enableSorting: false,
				cell: renderInputCell('name', 'text'),
				size: '2fr',
			},
			{
				id: 'color',
				accessor: 'color',
				header: 'Color',
				enableSorting: false,
				cell: renderColorCell(),
				size: '80px',
			},
		],
		[renderColorCell, renderInputCell, renderTextCell]
	);

	const headerButtons = React.useMemo<FooterButton[]>(
		() => [
			{ iconName: 'ActionPlusSmall', label: 'Add Billing Code Group', onClick: addNewGroup },
			{ iconName: 'FilterSort', label: 'Sort Billing Code Groups', onClick: openSortModal },
		],
		[addNewGroup, openSortModal]
	);

	const onRemoveGroup = React.useCallback(() => {
		requestAnimationFrame(reindex);
	}, [reindex]);

	const billingCodeGroupsWatch = useWatch({
		control,
		name: 'billingCodeGroups',
	});

	// We need this so the table re-renders when the order of the groups changes
	const orderKey = React.useMemo(() => {
		return (billingCodeGroupsWatch ?? [])
			.map((g) => `${g.id}:${g.sortIndex}:${g.color}`)
			.join('|');
	}, [billingCodeGroupsWatch]);

	return (
		<div>
			<SimpleTableRHFWrapper
				columns={columns}
				emptyTableMessage="No Billing Code Groups found."
				fields={fields.filter((f) => !f.deletedAt)}
				formAccessorName="billingCodeGroups"
				headerButtons={headerButtons}
				key={orderKey}
				label="Billing Code Groups"
				// labelTooltip="A categorization layer for organizing related billing codes. In the Job Tracking table, codes are grouped and sorted
				// by their associated group, as listed here."
				onRemove={onRemoveGroup}
				remove={remove}
			/>
			<SortGroupsModal
				close={closeSortModal}
				groups={billingCodeGroupsWatch}
				onSave={onSaveSortGroups}
				show={showSortModal}
			/>
		</div>

	);
});

export default BillingCodeGroupsArray;
